/**
 * Validation utilities for forms and application steps
 *
 * This module provides validation functions that return error keys instead of messages,
 * allowing for easy internationalization and consistent error handling.
 */

/**
 * Interface for validation errors object
 */
export interface ValidationErrors {
	[key: string]: string | undefined;
}

/**
 * Interface for validation result object
 */
export interface ValidationResult {
	isValid: boolean;
	errors: ValidationErrors;
}

/**
 * Validation error keys (separate from messages for i18n support)
 * These keys are used by validation functions and mapped to user-friendly messages
 */
export const VALIDATION_ERRORS = {
	REQUIRED: 'required',
	EMAIL_INVALID: 'email_invalid',
	PHONE_INVALID: 'phone_invalid',
	CONTACT_INFO_REQUIRED: 'contact_info_required',
	WAGE_SUM_NOT_NUMERIC: 'wage_sum_not_numeric',
	WAGE_SUM_NOT_POSITIVE: 'wage_sum_not_positive',
	WAGE_SUM_TOO_LONG: 'wage_sum_too_long',
	KVK_INVALID: 'kvk_invalid',
	SECTOR_CODE_INVALID: 'sector_code_invalid',
	SECTOR_CODE_TOO_LONG: 'sector_code_too_long',
	PREMIUM_PERCENTAGE_INVALID: 'premium_percentage_invalid',
	PREMIUM_PERCENTAGE_OUT_OF_RANGE: 'premium_percentage_out_of_range'
} as const;

/**
 * Validation messages mapped to error keys
 * Supports both static strings and functions for dynamic messages
 */
export const VALIDATION_MESSAGES: Record<string, string | ((fieldName: string) => string)> = {
	[VALIDATION_ERRORS.REQUIRED]: 'Dit is een verplicht veld',
	[VALIDATION_ERRORS.EMAIL_INVALID]: 'Voer een geldig e-mailadres in',
	[VALIDATION_ERRORS.PHONE_INVALID]: 'Voer een geldig Nederlands telefoonnummer in',
	[VALIDATION_ERRORS.CONTACT_INFO_REQUIRED]: 'Vul je telefoonnummer of e-mailadres in',
	[VALIDATION_ERRORS.WAGE_SUM_NOT_NUMERIC]: 'Loonsom moet een getal zijn',
	[VALIDATION_ERRORS.WAGE_SUM_NOT_POSITIVE]: 'Loonsom moet groter zijn dan 0',
	[VALIDATION_ERRORS.WAGE_SUM_TOO_LONG]: 'Loonsom mag maximaal 8 cijfers bevatten',
	[VALIDATION_ERRORS.KVK_INVALID]: 'KvK nummer is verplicht',
	[VALIDATION_ERRORS.SECTOR_CODE_INVALID]: 'Sectorcode moet een getal zijn',
	[VALIDATION_ERRORS.SECTOR_CODE_TOO_LONG]: 'Sectorcode mag maximaal 3 cijfers bevatten',
	[VALIDATION_ERRORS.PREMIUM_PERCENTAGE_INVALID]: 'Premiepercentage moet een geldig getal zijn',
	[VALIDATION_ERRORS.PREMIUM_PERCENTAGE_OUT_OF_RANGE]:
		'Premiepercentage moet tussen 0 en 100 liggen'
};

/**
 * Validates if a required field has a value
 * @param value - The field value to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateRequired = (value: string): string | undefined => {
	if (!value?.trim()) {
		return VALIDATION_ERRORS.REQUIRED;
	}
	return undefined;
};

/**
 * Validates email format and required field
 * @param email - The email string to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateEmail = (email: string): string | undefined => {
	if (!email?.trim()) {
		return VALIDATION_ERRORS.REQUIRED;
	}
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.includes('..')) {
		return VALIDATION_ERRORS.EMAIL_INVALID;
	}
	return undefined;
};

/**
 * Validates Dutch phone numbers in local or international format
 * Local: 06xxxxxxxx (mobile) or 0[1-9]xxxxxxxx (landline)
 * International: +316xxxxxxxx or 00316xxxxxxxx (mobile), +31[1-9]xxxxxxxx or 0031[1-9]xxxxxxxx (landline)
 * @param phone - The phone number string to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validatePhone = (phone: string): string | undefined => {
	if (!phone?.trim()) {
		return VALIDATION_ERRORS.REQUIRED;
	}

	// Remove all non-digit characters for validation
	const cleanPhone = phone.replace(/[^0-9]/g, '');

	// Patterns: local (10 digits), international +31 (11 digits), international 0031 (13 digits)
	const isValid = /^(06\d{8}|0[1-9]\d{8}|316\d{8}|31[1-9]\d{8}|00316\d{8}|0031[1-9]\d{8})$/.test(
		cleanPhone
	);

	return isValid ? undefined : VALIDATION_ERRORS.PHONE_INVALID;
};

/**
 * Converts validation error key to user-friendly message
 * @param errorKey - The error key from VALIDATION_ERRORS
 * @param fieldName - Optional field name for dynamic messages (legacy support)
 * @returns Localized error message or the error key if not found
 */
export const getValidationMessage = (errorKey: string, fieldName?: string): string => {
	const message = VALIDATION_MESSAGES[errorKey as keyof typeof VALIDATION_MESSAGES];
	if (typeof message === 'function') {
		return message(fieldName || '');
	}
	if (typeof message === 'string') {
		return message;
	}
	return errorKey;
};

/**
 * Validates employee name field
 * @param name - The name value to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateEmployeeName = (name?: string): string | undefined => {
	if (!name?.trim()) {
		return VALIDATION_ERRORS.REQUIRED;
	}
	return undefined;
};

/**
 * Validates employee wage field
 * @param wage - The wage value to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateEmployeeWage = (wage?: number | null): string | undefined => {
	if (wage === null || wage === undefined || wage === 0) {
		return VALIDATION_ERRORS.REQUIRED;
	}
	return undefined;
};

/**
 * Validates employee gender field
 * @param gender - The gender value to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateEmployeeGender = (gender?: string): string | undefined => {
	if (!gender || (gender !== 'm' && gender !== 'v')) {
		return VALIDATION_ERRORS.REQUIRED;
	}
	return undefined;
};

/**
 * Validates employee contract type field
 * @param contractType - The contract type value to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateEmployeeContractType = (contractType?: string): string | undefined => {
	if (!contractType || contractType === '') {
		return VALIDATION_ERRORS.REQUIRED;
	}
	return undefined;
};

/**
 * Validates employee birth date field
 * @param birthDate - The birth date value to validate (YYYY or YYYY-MM-DD format)
 * @returns Error key if invalid, undefined if valid
 */
export const validateEmployeeBirthDate = (birthDate?: string): string | undefined => {
	if (!birthDate || birthDate.trim() === '') {
		return VALIDATION_ERRORS.REQUIRED;
	}
	// Birth date should be at least 4 characters (YYYY format)
	if (birthDate.length < 4) {
		return VALIDATION_ERRORS.REQUIRED;
	}
	return undefined;
};

/**
 * Validates wage sum field for policy upload forms
 * Validates that the wage sum is required, numeric, positive, and within length limits
 * @param wageSum - The wage sum value to validate
 * @param maxLength - Maximum allowed length (default: 8)
 * @returns Error key if invalid, undefined if valid
 */
export const validateWageSum = (wageSum?: string, maxLength = 8): string | undefined => {
	// Check if field is required
	if (!wageSum?.trim()) {
		return VALIDATION_ERRORS.REQUIRED;
	}

	const trimmedValue = wageSum.trim();

	// Check if value is numeric (digits only, no decimals)
	if (!/^\d+$/.test(trimmedValue)) {
		return VALIDATION_ERRORS.WAGE_SUM_NOT_NUMERIC;
	}

	// Check if value is positive
	const numericValue = Number.parseInt(trimmedValue, 10);
	if (numericValue <= 0) {
		return VALIDATION_ERRORS.WAGE_SUM_NOT_POSITIVE;
	}

	// Check if value exceeds maximum length
	if (trimmedValue.length > maxLength) {
		return VALIDATION_ERRORS.WAGE_SUM_TOO_LONG;
	}

	return undefined;
};

/**
 * Checks if a value matches the format of a Dutch KvK number (8 digits)
 * Note: This only validates the format, not whether the KvK number actually exists
 * @param value - The value to check
 * @returns True if the value is exactly 8 digits, false otherwise
 */
export const isValidKvKNumber = (value: string): boolean => {
	return /^\d{8}$/.test(value.trim());
};

/**
 * Validates KvK (Chamber of Commerce) number field
 * Validates that the KvK number is required and exactly 8 digits
 * @param kvk - The KvK number value to validate
 * @returns Error key if invalid, undefined if valid
 */
export const validateKvK = (kvk?: string): string | undefined => {
	// Check if field is required
	if (!kvk?.trim()) {
		return VALIDATION_ERRORS.KVK_INVALID;
	}

	// Check if value matches KvK format (exactly 8 digits)
	if (!isValidKvKNumber(kvk)) {
		return VALIDATION_ERRORS.KVK_INVALID;
	}

	return undefined;
};

/**
 * Validates sector code field
 * Validates that the sector code is required, contains only digits, and is max 3 characters
 * @param sectorCode - The sector code value to validate
 * @param maxLength - Maximum allowed length (default: 3)
 * @returns Error key if invalid, undefined if valid
 */
export const validateSectorCode = (sectorCode?: string, maxLength = 3): string | undefined => {
	// Check if field is required
	if (!sectorCode?.trim()) {
		return VALIDATION_ERRORS.REQUIRED;
	}

	const trimmedValue = sectorCode.trim();

	// Check if value contains only digits (no spaces allowed)
	if (!/^\d+$/.test(trimmedValue)) {
		return VALIDATION_ERRORS.SECTOR_CODE_INVALID;
	}

	// Check if value exceeds maximum length
	if (trimmedValue.length > maxLength) {
		return VALIDATION_ERRORS.SECTOR_CODE_TOO_LONG;
	}

	return undefined;
};

/**
 * Validates absence percentage input during onChange
 * Checks if the input is a valid number with optional decimal separator (comma or period) and within range 0-99.9
 * Supports Dutch notation where comma is used as decimal separator (e.g., "3,2" instead of "3.2")
 * @param value - The input value to validate
 * @param minValue - Minimum allowed value (default: 0)
 * @param maxValue - Maximum allowed value (default: 99.9)
 * @returns True if value is valid or empty, false otherwise
 */
export const validateAbsencePercentageInput = (
	value: string,
	minValue = 0,
	maxValue = 99.9
): boolean => {
	// Empty string is always valid (allows deletion)
	if (value === '') {
		return true;
	}

	// Check if value matches pattern: digits with optional single decimal separator (comma or period)
	if (!/^[0-9]*[.,]?[0-9]*$/.test(value)) {
		return false;
	}

	// Normalize comma to period for parsing
	const normalizedValue = value.replace(',', '.');

	// If we have a parseable number, check range
	const numValue = Number.parseFloat(normalizedValue);
	if (!Number.isNaN(numValue)) {
		return numValue >= minValue && numValue <= maxValue;
	}

	// Allow partial input like "3," or "3." or "," or "." during typing
	return true;
};

/**
 * Formats absence percentage value on blur
 * Converts the input to a properly formatted decimal value with one decimal place
 * Uses Dutch notation with comma as decimal separator (e.g., "3,2" instead of "3.2")
 * @param value - The input value to format
 * @returns Formatted value (empty string if invalid, or number formatted to 1 decimal place with comma)
 */
export const formatAbsencePercentageOnBlur = (value: string): string => {
	// Return empty for empty input
	if (value === '') {
		return '';
	}

	// Normalize comma to period for parsing (accept both input formats)
	const normalizedValue = value.replace(',', '.');

	// Try to parse as number
	const numValue = Number.parseFloat(normalizedValue);

	// Return empty if not a valid number
	if (Number.isNaN(numValue)) {
		return '';
	}

	// Format to one decimal place with period as decimal separator
	return numValue.toFixed(1);
};

/**
 * Validates premium percentage field for policy upload forms
 * Validates that the premium percentage is optional, numeric, and within range 0-100
 * @param premiumPercentage - The premium percentage value to validate (optional field)
 * @returns Error key if invalid, undefined if valid
 */
export const validatePremiumPercentage = (premiumPercentage?: string): string | undefined => {
	// Field is optional - empty is valid
	if (!premiumPercentage?.trim()) {
		return undefined;
	}

	const trimmedValue = premiumPercentage.trim();

	// Check if value is numeric (allows decimal point)
	if (!/^[0-9]+\.?[0-9]*$/.test(trimmedValue)) {
		return VALIDATION_ERRORS.PREMIUM_PERCENTAGE_INVALID;
	}

	// Check if value is within range (0-100)
	const numericValue = Number.parseFloat(trimmedValue);
	if (Number.isNaN(numericValue)) {
		return VALIDATION_ERRORS.PREMIUM_PERCENTAGE_INVALID;
	}

	if (numericValue < 0 || numericValue > 100) {
		return VALIDATION_ERRORS.PREMIUM_PERCENTAGE_OUT_OF_RANGE;
	}

	return undefined;
};
